import React from 'react';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Mock statistics
  const stats = [
    { label: 'Open Tickets', value: '12', color: '#ef4444' },
    { label: 'Pending', value: '8', color: '#f97316' },
    { label: 'Resolved', value: '456', color: '#10b981' },
    { label: 'Response Time', value: '2.5h', color: '#1e3a8a' },
  ];

  const recentTickets = [
    {
      id: 'TK-001234',
      title: 'System downtime error',
      status: 'Open',
      priority: 'High',
      updated: '10 mins',
    },
    {
      id: 'TK-001233',
      title: 'Password reset request',
      status: 'In Progress',
      priority: 'Medium',
      updated: '1 hour',
    },
    {
      id: 'TK-001232',
      title: 'Email configuration',
      status: 'Resolved',
      priority: 'Low',
      updated: '3 hours',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.greeting}>
              ยินดีต้อนรับ, {user?.full_name}
            </h1>
            <p style={styles.subtext}>
              {user?.role === 'admin'
                ? 'Admin Dashboard'
                : 'Support Agent Dashboard'}
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={styles.logoutBtn}
            title="Logout"
          >
            ออกจากระบบ
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.content}>
        {/* Statistics */}
        <section style={styles.statsSection}>
          <h2 style={styles.sectionTitle}>สถิติวันนี้</h2>
          <div style={styles.statsGrid}>
            {stats.map((stat, idx) => (
              <div key={idx} style={styles.statCard}>
                <div style={styles.statHeader}>
                  <span style={styles.statIcon}>{stat.icon}</span>
                  <p style={styles.statLabel}>{stat.label}</p>
                </div>
                <p style={styles.statValue}>{stat.value}</p>
                <div
                  style={{
                    ...styles.statBar,
                    backgroundColor: stat.color,
                  }}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Recent Tickets */}
        <section style={styles.recentSection}>
          <div style={styles.recentHeader}>
            <h2 style={styles.sectionTitle}>ตั๋วล่าสุด</h2>
            <button
              onClick={() => navigate('/tickets')}
              style={styles.viewAllBtn}
            >
              ดูทั้งหมด →
            </button>
          </div>
          <div style={styles.ticketsList}>
            {recentTickets.map((ticket) => (
              <div key={ticket.id} style={styles.ticketRow}>
                <div style={styles.ticketLeft}>
                  <h4 style={styles.ticketId}>{ticket.id}</h4>
                  <p style={styles.ticketTitle}>{ticket.title}</p>
                </div>
                <div style={styles.ticketCenter}>
                  <span
                    style={{
                      ...styles.priorityBadge,
                      ...(ticket.priority === 'High'
                        ? styles.priorityHigh
                        : ticket.priority === 'Medium'
                        ? styles.priorityMedium
                        : styles.priorityLow),
                    }}
                  >
                    {ticket.priority}
                  </span>
                </div>
                <div style={styles.ticketRight}>
                  <span style={styles.updated}>{ticket.updated}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fafbfc 0%, #f0f4ff 50%, #faf8ff 100%)',
    paddingBottom: '64px',
  },
  // Header Styles
  header: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 50%, #ffffff 100%)',
    borderBottom: '2px solid rgba(30, 58, 138, 0.12)',
    padding: '2.5rem 1.5rem',
    boxShadow: '0 8px 32px rgba(30, 58, 138, 0.12), 0 2px 8px rgba(0, 0, 0, 0.05)',
    position: 'relative',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  greeting: {
    fontSize: '2rem',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #0f2847 50%, #f97316 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: 0,
    letterSpacing: '-0.5px',
  },
  subtext: {
    fontSize: '0.925rem',
    color: '#64748b',
    margin: '0.5rem 0 0 0',
    fontWeight: '600',
    letterSpacing: '0.3px',
  },
  logoutBtn: {
    padding: '0.75rem 1.75rem',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '700',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'auto',
    boxShadow: '0 6px 20px rgba(239, 68, 68, 0.25)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  'logoutBtn:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 10px 30px rgba(239, 68, 68, 0.35)',
  },

  // Content Styles
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2.5rem 1.5rem 6rem 1.5rem',
  },

  // Stats Section
  statsSection: {
    marginBottom: '3rem',
  },
  sectionTitle: {
    fontSize: 'clamp(1.25rem, 4vw, 1.5rem)',
    fontWeight: '800',
    color: '#1f2937',
    marginBottom: '1.5rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1.25rem',
  },
  statCard: {
    background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 100%)',
    padding: '1.5rem',
    borderRadius: '14px',
    border: '1px solid rgba(30, 58, 138, 0.1)',
    boxShadow: '0 4px 20px rgba(30, 58, 138, 0.08), 0 1px 3px rgba(0, 0, 0, 0.02)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'auto',
    position: 'relative',
    overflow: 'hidden',
  },
  'statCard:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 32px rgba(30, 58, 138, 0.15), 0 1px 3px rgba(0, 0, 0, 0.02)',
    borderColor: 'rgba(30, 58, 138, 0.25)',
  },
  statHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  statIcon: {
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statLabel: {
    fontSize: '0.75rem',
    color: '#64748b',
    fontWeight: '700',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.2px',
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: '900',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #0f2847 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: '0.5rem 0 1rem 0',
    letterSpacing: '-1px',
  },
  statBar: {
    height: '3px',
    borderRadius: '3px',
    width: '100%',
  },

  // Actions Section
  actionsSection: {
    marginBottom: '3rem',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
  },
  actionCard: {
    background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%)',
    padding: '2rem 1.5rem',
    borderRadius: '14px',
    border: '1px solid rgba(59, 130, 246, 0.15)',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'auto',
    userSelect: 'none',
    fontFamily: 'inherit',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
  },
  'actionCard:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 40px rgba(59, 130, 246, 0.2)',
    borderColor: 'rgba(59, 130, 246, 0.4)',
  },
  'actionCard:active': {
    transform: 'translateY(-4px)',
  },
  actionLabel: {
    fontSize: '0.75rem',
    fontWeight: '700',
    display: 'block',
    marginBottom: '0.75rem',
    color: '#1e3a8a',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  'actionCard h3': {
    fontSize: '1.125rem',
    fontWeight: '800',
    color: '#1f2937',
    margin: '0.75rem 0 0.5rem 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  'actionCard p': {
    fontSize: '0.85rem',
    color: '#64748b',
    margin: '0.5rem 0 0 0',
    fontWeight: '500',
  },

  // Recent Tickets Section
  recentSection: {
    background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.95) 100%)',
    borderRadius: '14px',
    border: '1px solid rgba(59, 130, 246, 0.15)',
    padding: '2rem',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
  },
  recentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  viewAllBtn: {
    background: 'transparent',
    border: 'none',
    color: '#1e3a8a',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '700',
    pointerEvents: 'auto',
    transition: 'all 0.3s',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
  },
  'viewAllBtn:hover': {
    color: '#1e3a8a',
    backgroundColor: 'rgba(30, 58, 138, 0.1)',
  },
  ticketsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  ticketRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.5rem 1.25rem',
    background: 'linear-gradient(135deg, #fafbff 0%, #f5f8ff 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(30, 58, 138, 0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  'ticketRow:hover': {
    background: 'linear-gradient(135deg, #f0f4ff 0%, #ebf2ff 100%)',
    borderColor: 'rgba(30, 58, 138, 0.25)',
    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.08)',
    transform: 'translateX(4px)',
  },
  ticketLeft: {
    flex: 1,
  },
  ticketId: {
    fontSize: '0.875rem',
    fontWeight: '800',
    color: '#1f2937',
    margin: 0,
    marginBottom: '0.25rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  ticketTitle: {
    fontSize: '0.875rem',
    color: '#64748b',
    margin: 0,
    fontWeight: '500',
  },
  ticketCenter: {
    marginLeft: '1rem',
  },
  priorityBadge: {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  priorityHigh: {
    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    color: '#991b1b',
  },
  priorityMedium: {
    background: 'linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%)',
    color: '#92400e',
  },
  priorityLow: {
    background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
    color: '#166534',
  },
  ticketRight: {
    marginLeft: '1rem',
    textAlign: 'right',
  },
  updated: {
    fontSize: '0.75rem',
    color: '#94a3b8',
    fontWeight: '600',
  },
};
